import { useEffect, useRef, useState } from 'react';
import { Settings, Clock, Target, Brain, Sparkles, CheckCircle2, ToggleLeft } from 'lucide-react';

interface AIInterview3DProps {
  scrollY: number;
}

export default function AIInterview3D({ scrollY }: AIInterview3DProps) {
  const [buildProgress, setBuildProgress] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionRef.current) return;

    const sectionTop = sectionRef.current.offsetTop;
    const sectionHeight = sectionRef.current.offsetHeight;
    const progress = Math.max(0, Math.min(1, (scrollY - sectionTop + window.innerHeight / 2) / sectionHeight));

    setBuildProgress(progress);
  }, [scrollY]);

  return (
    <div ref={sectionRef} className="relative py-32 overflow-hidden bg-transparent">

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 glass-dark rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-medium text-white">AI-Powered Technology</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6 text-white">
            <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Granular Configuration
            </span>
            <span className="block mt-2">of Each Interview</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            Fine-tune every aspect of your interview process with precision control over timing, difficulty, skills assessment, and evaluation criteria
          </p>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div
                className="relative"
                style={{
                  transform: `perspective(1200px) rotateX(${5 - buildProgress * 5}deg)`,
                  transition: 'transform 0.3s ease-out'
                }}
              >
                <div className="glass-dark rounded-3xl shadow-2xl overflow-hidden border border-emerald-500/20">
                  <div className="bg-slate-800/80 backdrop-blur-xl px-6 py-4 flex items-center justify-between border-b border-slate-700/50">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Settings className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm text-slate-400">Interview Configuration</span>
                    </div>
                  </div>

                  <div className="p-8 space-y-6">
                    <div className="glass-dark rounded-2xl p-6 border border-emerald-500/20">
                      <div className="flex items-center gap-3 mb-4">
                        <Clock className="w-5 h-5 text-emerald-400" />
                        <h3 className="text-white font-bold">Interview Duration & Timing</h3>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-slate-400 mb-2 block">Total Duration</label>
                          <div className="bg-slate-800/50 rounded-lg px-4 py-3 border border-slate-700">
                            <span className="text-white font-semibold">45 minutes</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-2 block">Question Time Limit</label>
                          <div className="bg-slate-800/50 rounded-lg px-4 py-3 border border-slate-700">
                            <span className="text-white font-semibold">3 minutes</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="text-xs text-slate-400 mb-2 block">Adaptive Timing</label>
                        <div className="flex items-center gap-3">
                          <ToggleLeft className="w-8 h-8 text-emerald-400" />
                          <span className="text-sm text-slate-300">Enabled - Adjust time based on candidate performance</span>
                        </div>
                      </div>
                    </div>

                    <div className="glass-dark rounded-2xl p-6 border border-teal-500/20">
                      <div className="flex items-center gap-3 mb-4">
                        <Target className="w-5 h-5 text-teal-400" />
                        <h3 className="text-white font-bold">Difficulty & Complexity</h3>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-400">Starting Difficulty</span>
                            <span className="text-emerald-400 font-semibold">Medium</span>
                          </div>
                          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full w-3/5"></div>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-2 block">Adaptive Difficulty</label>
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            <span className="text-sm text-slate-300">Auto-adjust based on responses</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-2 block">Challenge Progression</label>
                          <div className="bg-slate-800/50 rounded-lg px-4 py-3 border border-slate-700">
                            <span className="text-white">Easy → Medium → Hard</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="glass-dark rounded-2xl p-6 border border-cyan-500/20">
                      <div className="flex items-center gap-3 mb-4">
                        <Brain className="w-5 h-5 text-cyan-400" />
                        <h3 className="text-white font-bold">Skills Assessment Focus</h3>
                      </div>
                      <div className="space-y-3">
                        {['Technical Proficiency', 'Problem Solving', 'Communication', 'Domain Knowledge'].map((skill, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                            <span className="text-slate-300">{skill}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full"
                                  style={{ width: `${85 - i * 10}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-cyan-400 font-semibold w-8">{85 - i * 10}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="glass-dark rounded-3xl p-6 border border-emerald-500/20">
                <h4 className="text-sm font-semibold text-slate-400 mb-4">DOMAIN CASE STUDIES</h4>
                <div className="space-y-3">
                  <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                    <div className="text-white font-semibold mb-1">Technology & IT</div>
                    <div className="text-xs text-slate-400 mt-1">Full-stack, DevOps, Security</div>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                    <div className="text-white font-semibold mb-1">Finance & Banking</div>
                    <div className="text-xs text-slate-400 mt-1">Risk, Trading, Operations</div>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                    <div className="text-white font-semibold mb-1">Healthcare & Pharma</div>
                    <div className="text-xs text-slate-400 mt-1">Clinical, Research, Medical</div>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                    <div className="text-white font-semibold mb-1">Retail & E-commerce</div>
                    <div className="text-xs text-slate-400 mt-1">Sales, Supply Chain, CX</div>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                    <div className="text-white font-semibold mb-1">Manufacturing</div>
                    <div className="text-xs text-slate-400 mt-1">Quality, Production, Lean</div>
                  </div>
                </div>
              </div>

              <div className="glass-dark rounded-3xl p-6 border border-teal-500/20">
                <h4 className="text-sm font-semibold text-slate-400 mb-4">EVALUATION CRITERIA</h4>
                <div className="space-y-3">
                  {[
                    { label: 'Response Quality', weight: '30%' },
                    { label: 'Technical Accuracy', weight: '25%' },
                    { label: 'Communication', weight: '20%' },
                    { label: 'Problem Approach', weight: '15%' },
                    { label: 'Time Management', weight: '10%' }
                  ].map((criteria, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-sm text-slate-300">{criteria.label}</span>
                      <span className="text-sm font-bold text-emerald-400">{criteria.weight}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-dark rounded-3xl p-6 border border-cyan-500/20">
                <h4 className="text-sm font-semibold text-slate-400 mb-4">REAL-TIME ADJUSTMENTS</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Dynamic question selection</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Difficulty auto-adjustment</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Follow-up question generation</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Skill-gap identification</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
